import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, Package, QrCode, LogIn } from 'lucide-react';

/**
 * ScanRedirectPage - Handles QR code scans from external scanners
 * 
 * When someone scans an asset QR code with their phone's camera:
 * - If logged in: redirects to the asset detail page
 * - If not logged in: shows login prompt, then redirects after login
 */
export const ScanRedirectPage = () => {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (!loading && isAuthenticated && assetId) {
      setRedirecting(true);
      // Small delay to show the redirecting message
      const timer = setTimeout(() => {
        navigate(`/assets?view=${assetId}`, { replace: true });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, loading, assetId, navigate]);

  // Store the intended destination for after login
  useEffect(() => {
    if (!loading && !isAuthenticated && assetId) {
      sessionStorage.setItem('scan_redirect', assetId);
    }
  }, [isAuthenticated, loading, assetId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (redirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-lg font-medium">Opening asset details...</p>
          <p className="text-muted-foreground text-sm mt-1">Please wait</p>
        </div>
      </div>
    );
  }

  // Not authenticated - show login prompt
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <QrCode className="w-8 h-8 text-primary" />
          </div>
          
          <h1 className="text-2xl font-bold mb-2">Asset QR Code Scanned</h1>
          <p className="text-muted-foreground mb-6">
            Sign in to EQUIPR Guardian to view this asset's details, inspection history, and more.
          </p>

          <div className="bg-secondary/50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center gap-2 text-sm">
              <Package className="w-4 h-4 text-primary" />
              <span className="font-mono text-xs">{assetId}</span>
            </div>
          </div>

          <Button 
            className="w-full btn-industrial" 
            size="lg"
            onClick={() => navigate('/login', { state: { from: `/scan/${assetId}` } })}
          >
            <LogIn className="w-5 h-5 mr-2" />
            Sign In to Continue
          </Button>

          <p className="text-xs text-muted-foreground mt-4">
            Don't have an account? Contact your administrator.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ScanRedirectPage;
